import { useEffect, useRef } from "react";

import { Space } from "components/icons/generated";
import ListEntitiesItem from "components/ListEntitiesItem";
import ListEntitiesItemLink from "components/ListEntitiesItem/Link";
import MetaInfoListItem from "components/MetaInfoList/Item";
import ProjectTypeBadge, { ProjectType } from "components/ProjectTypeBadge";
import Box from "ds/components/Box";
import { PolicyIntentProjectAttachment, PolicyStackAttachment } from "types/generated";

import { COLUMN_GAP, COLUMN_ORDER } from "./constants";

type PolicyDetailsUsedByEntityProps = {
  attachedItem: PolicyStackAttachment | PolicyIntentProjectAttachment;
  setRowHeight?: (size: number) => void;
};

const PolicyDetailsUsedByEntity = ({
  attachedItem,
  setRowHeight,
}: PolicyDetailsUsedByEntityProps) => {
  const rowRef = useRef<HTMLDivElement>(null);

  const handleRowHeight = () => {
    if (setRowHeight && rowRef.current) {
      setRowHeight(rowRef.current.getBoundingClientRect().height);
    }
  };

  const isStack =
    attachedItem.__typename === "PolicyStackAttachment" && "stackName" in attachedItem;
  const isModule = isStack && attachedItem.isModule;
  const isIntent = "projectName" in attachedItem;

  let badge: ProjectType;
  if (isStack) {
    badge = ProjectType.Stack;
  } else if (isModule) {
    badge = ProjectType.Module;
  } else {
    badge = ProjectType.Intent;
  }

  let href = "";
  if (isStack) {
    href = `/stack/${attachedItem.stackId}`;
  } else if (isModule) {
    href = `/module/${attachedItem.stackId}`;
  } else if (isIntent) {
    href = `/intent-project/${attachedItem.projectID}`;
  }

  const spaceDetails = isIntent ? attachedItem.projectSpaceDetails : attachedItem.stackSpaceDetails;

  // on every render, update the row height
  useEffect(handleRowHeight);

  return (
    <ListEntitiesItem
      direction="row"
      align="center"
      justify="between"
      grid
      gridTemplate={COLUMN_ORDER}
      gap={`0 ${COLUMN_GAP}`}
      ref={rowRef}
    >
      <Box>
        <ProjectTypeBadge projectType={badge} />
      </Box>
      <Box align="center">
        <ListEntitiesItemLink
          title={isIntent ? attachedItem.projectName : attachedItem.stackName}
          titleTag="h3"
          href={href}
        />
      </Box>

      <MetaInfoListItem
        icon={Space}
        linkText={spaceDetails.name}
        href={`/spaces/${spaceDetails.id}`}
      />
    </ListEntitiesItem>
  );
};

export default PolicyDetailsUsedByEntity;
